import { ActionContext } from 'vuex';
import {
  SET_TOOLTIP_VISIBILITY,
  SET_TOOLTIP_CURRENT,
  SET_TOOLTIP_STEP,
  SET_USER_PREFERENCES,
  COMPLETE_TOOLTIP,
  SKIP_TOOLTIP
} from "./constants";
import { TooltipModuleState } from "./interfaces";
import { shouldShowTooltip, getTooltipConfig } from "@/utils/tooltip-configs";

const TOOLTIP_PREFERENCES_KEY = "hcms_tooltip_preferences";
const COMPLETED_TOURS_KEY = "hcms_completed_tours";
const DISMISSED_TOOLTIPS_KEY = "hcms_dismissed_tooltips";
const FIRST_TIME_LOGIN_KEY = "hcms_first_time_login";

// TypeScript interfaces for action payloads
interface CompleteTooltipPayload {
  tooltipId?: string;
}

interface SkipTooltipPayload {
  tooltipId?: string;
}

export default {
  /**
   * Initialize tooltip system on app startup
   */
  initTooltips({ commit, dispatch }: ActionContext<TooltipModuleState, any>, { userRole, routePath }: { userRole: string; routePath: string }): void {
    try {
      // Check if this is the first time login in this browser
      const isFirstTimeInBrowser = !localStorage.getItem(FIRST_TIME_LOGIN_KEY);
      
      // Load user preferences from localStorage
      const savedPreferences = localStorage.getItem(TOOLTIP_PREFERENCES_KEY);
      const completedTours = JSON.parse(localStorage.getItem(COMPLETED_TOURS_KEY) || '[]');
      const dismissedTooltips = JSON.parse(localStorage.getItem(DISMISSED_TOOLTIPS_KEY) || '[]');

      let userPreferences: any;
      if (savedPreferences) {
        userPreferences = JSON.parse(savedPreferences);
        // Override firstRun based on browser-based detection
        userPreferences.firstRun = isFirstTimeInBrowser;
        commit(SET_USER_PREFERENCES, userPreferences);
      } else {
        // Set default preferences for new users
        userPreferences = {
          firstRun: isFirstTimeInBrowser,
          completedTours,
          dismissedTooltips
        };
        commit(SET_USER_PREFERENCES, userPreferences);
      }

      // Mark that user has logged in to this browser
      if (isFirstTimeInBrowser) {
        localStorage.setItem(FIRST_TIME_LOGIN_KEY, 'true');
      }

      // Check if we should show any tooltips for current route
      setTimeout(() => {
        // Always check welcome tooltips (they will only show if firstRun is true)
        dispatch('showWelcomeTooltips', { userRole, routePath });
        // Check for other route-specific tooltips (but not on first login)
        if (!userPreferences.firstRun) {
          dispatch('checkRouteTooltips', { userRole, routePath });
        }
      }, 1000); // Delay to ensure page is rendered

    } catch (error) {
      console.error('Error initializing tooltips:', error);
    }
  },

  /**
   * Show welcome tooltips for first-time users
   */
  showWelcomeTooltips({ commit, state }: ActionContext<TooltipModuleState, any>, { userRole, routePath }: { userRole: string; routePath: string }): void {
    if (!state.userPreferences.firstRun) {
      return;
    }

    const welcomeConfig = getTooltipConfig(userRole, routePath, true);
    if (welcomeConfig) {
      commit(SET_TOOLTIP_CURRENT, { tooltipId: welcomeConfig.id });
      commit(SET_TOOLTIP_VISIBILITY, { visible: true, tooltipId: welcomeConfig.id });
      
      // Don't mark as complete here - wait for user to finish or skip
    }
  },

  /**
   * Show specific tooltip by ID
   */
  showTooltip({ commit, state }: ActionContext<TooltipModuleState, any>, { tooltipId, userRole, routePath }: { tooltipId: string; userRole: string; routePath: string }): void {
    const shouldShow = shouldShowTooltip(
      tooltipId,
      userRole,
      routePath,
      [...state.userPreferences.completedTours, ...state.userPreferences.dismissedTooltips],
      false
    );

    if (shouldShow) {
      commit(SET_TOOLTIP_CURRENT, { tooltipId });
      commit(SET_TOOLTIP_VISIBILITY, { visible: true, tooltipId });
    }
  },

  /**
   * Hide current tooltip
   */
  hideTooltip({ commit }: ActionContext<TooltipModuleState, any>): void {
    commit(SET_TOOLTIP_VISIBILITY, { visible: false });
  },

  /**
   * Go to next step in current tooltip
   */
  nextTooltipStep({ commit, state }: ActionContext<TooltipModuleState, any>): void {
    const currentTooltipConfig = getTooltipConfig(
      'recruiter', // This will need to be dynamic based on actual user role
      state.tooltips.currentTooltipId || '',
      false
    );

    if (currentTooltipConfig && state.tooltips.currentStep < currentTooltipConfig.steps.length - 1) {
      commit(SET_TOOLTIP_STEP, state.tooltips.currentStep + 1);
    }
  },

  /**
   * Go to previous step in current tooltip
   */
  previousTooltipStep({ commit, state }: ActionContext<TooltipModuleState, any>): void {
    if (state.tooltips.currentStep > 0) {
      commit(SET_TOOLTIP_STEP, state.tooltips.currentStep - 1);
    }
  },

  /**
   * Complete current tooltip tour
   */
  completeTooltip({ commit, state }: ActionContext<TooltipModuleState, any>, payload?: CompleteTooltipPayload): void {
    const tooltipId = payload?.tooltipId || state.tooltips.currentTooltipId;
    
    if (tooltipId) {
      commit(COMPLETE_TOOLTIP, { tooltipId });
      
      // Update localStorage
      const updatedCompletedTours = [...state.userPreferences.completedTours, tooltipId];
      const updatedPreferences = {
        ...state.userPreferences,
        completedTours: updatedCompletedTours,
        firstRun: false // Mark as no longer first run
      };
      
      commit(SET_USER_PREFERENCES, updatedPreferences);
      localStorage.setItem(COMPLETED_TOURS_KEY, JSON.stringify(updatedCompletedTours));
      localStorage.setItem(TOOLTIP_PREFERENCES_KEY, JSON.stringify(updatedPreferences));
    }
  },

  /**
   * Skip current tooltip tour
   */
  skipTooltip({ commit, state }: ActionContext<TooltipModuleState, any>, payload?: SkipTooltipPayload): void {
    const tooltipId = payload?.tooltipId || state.tooltips.currentTooltipId;
    
    if (tooltipId) {
      commit(SKIP_TOOLTIP, { tooltipId });
      
      // Update localStorage
      const updatedDismissedTooltips = [...state.userPreferences.dismissedTooltips, tooltipId];
      const updatedPreferences = {
        ...state.userPreferences,
        dismissedTooltips: updatedDismissedTooltips,
        firstRun: false // Mark as no longer first run
      };
      
      commit(SET_USER_PREFERENCES, updatedPreferences);
      localStorage.setItem(DISMISSED_TOOLTIPS_KEY, JSON.stringify(updatedDismissedTooltips));
      localStorage.setItem(TOOLTIP_PREFERENCES_KEY, JSON.stringify(updatedPreferences));
    }
  },

  /**
   * Reset tooltip preferences (useful for testing/development)
   */
  resetTooltipPreferences({ commit }: ActionContext<TooltipModuleState, any>): void {
    localStorage.removeItem(TOOLTIP_PREFERENCES_KEY);
    localStorage.removeItem(COMPLETED_TOURS_KEY);
    localStorage.removeItem(DISMISSED_TOOLTIPS_KEY);
    
    commit(SET_USER_PREFERENCES, {
      firstRun: true,
      completedTours: [],
      dismissedTooltips: []
    });
  },

  /**
   * Set default preferences when none exist
   */
  defaultPreferences({ commit }: ActionContext<TooltipModuleState, any>, preferences: Partial<TooltipModuleState['userPreferences']>): void {
    commit(SET_USER_PREFERENCES, preferences);
  },

  /**
   * Check for tooltips on route changes
   */
  checkRouteTooltips({ commit, state }: ActionContext<TooltipModuleState, any>, { userRole, routePath }: { userRole: string; routePath: string }): void {
    // Check if we should show post-job tooltips
    const shouldShowPostJob = shouldShowTooltip(
      'post-job-tour',
      userRole,
      routePath,
      [...state.userPreferences.completedTours, ...state.userPreferences.dismissedTooltips],
      false
    );

    if (shouldShowPostJob) {
      setTimeout(() => {
        commit(SET_TOOLTIP_CURRENT, { tooltipId: 'post-job-tour' });
        commit(SET_TOOLTIP_VISIBILITY, { visible: true, tooltipId: 'post-job-tour' });
      }, 1000); // Delay to ensure page is rendered
      return; // Don't check other tooltips if post-job is showing
    }

    // Check if we should show job listing tooltips
    const shouldShowJobListing = shouldShowTooltip(
      'job-listing-tour',
      userRole,
      routePath,
      [...state.userPreferences.completedTours, ...state.userPreferences.dismissedTooltips],
      false
    );

    if (shouldShowJobListing) {
      setTimeout(() => {
        commit(SET_TOOLTIP_CURRENT, { tooltipId: 'job-listing-tour' });
        commit(SET_TOOLTIP_VISIBILITY, { visible: true, tooltipId: 'job-listing-tour' });
      }, 1000); // Delay to ensure page is rendered
      return; // Don't check other tooltips if job listing is showing
    }

    // Check if we should show job details tooltips
    const shouldShowJobDetails = shouldShowTooltip(
      'job-details-tour',
      userRole,
      routePath,
      [...state.userPreferences.completedTours, ...state.userPreferences.dismissedTooltips],
      false
    );

    if (shouldShowJobDetails) {
      setTimeout(() => {
        commit(SET_TOOLTIP_CURRENT, { tooltipId: 'job-details-tour' });
        commit(SET_TOOLTIP_VISIBILITY, { visible: true, tooltipId: 'job-details-tour' });
      }, 1000); // Delay to ensure page is rendered
    }
  }
};